import React, { useEffect, useMemo, useRef, useState } from "react";
import { L as Link, useLocaleNavigate } from "../lib/i18n";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Building2, ShieldCheck, Upload, ExternalLink, Loader2, Save, Sparkles,
  ImagePlus, Phone, MessageCircle, Globe, MapPin, Clock, Share2, Check,
  ChevronLeft, ChevronRight, Eye, X,
} from "lucide-react";

/* Phase 59 — Dealer Onboarding Wizard
   Multi-step, autosaves every step, mobile-first.
*/

const WEEKDAYS = [
  { key: "Mo", label: "Montag" }, { key: "Di", label: "Dienstag" }, { key: "Mi", label: "Mittwoch" },
  { key: "Do", label: "Donnerstag" }, { key: "Fr", label: "Freitag" }, { key: "Sa", label: "Samstag" },
  { key: "So", label: "Sonntag" },
];
const DEFAULT_HOURS = WEEKDAYS.map((d) => ({ day: d.key, closed: d.key === "So", open: "08:00", close: "18:00" }));

const STEPS = [
  { id: 1, key: "identity", label: "Identität", icon: Building2 },
  { id: 2, key: "story", label: "Über uns", icon: Sparkles },
  { id: 3, key: "contact", label: "Kontakt", icon: Phone },
  { id: 4, key: "location", label: "Standort", icon: MapPin },
  { id: 5, key: "hours", label: "Öffnungszeiten", icon: Clock },
  { id: 6, key: "social", label: "Social Media", icon: Share2 },
  { id: 7, key: "review", label: "Vorschau", icon: Eye },
];

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function ImageDrop({ value, onChange, label, aspect = "1/1", testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Nur Bilder erlaubt"); return; }
    if (file.size > 6 * 1024 * 1024) { toast.error("Bild zu gross (max 6 MB)"); return; }
    setBusy(true);
    try {
      const res = await api.upload(file);
      onChange(res?.path ? api.fileUrl(res.path) : res?.url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-2" data-testid={testid}>
      <Label className="text-zinc-200">{label}</Label>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
        className={classNames(
          "group relative cursor-pointer overflow-hidden rounded-xl border border-dashed border-zinc-700 bg-zinc-950 transition hover:border-red-500/60 hover:bg-zinc-900",
          "flex items-center justify-center"
        )}
        style={{ aspectRatio: aspect, minHeight: 120 }}
      >
        {value ? (
          <>
            <img src={value} alt={label} className="h-full w-full object-cover" />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(""); }}
              className="absolute right-2 top-2 rounded-full bg-black/70 p-1 text-white opacity-0 transition group-hover:opacity-100"
              data-testid={`${testid}-clear`}
            >
              <X className="h-4 w-4" />
            </button>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 text-zinc-500">
            {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <ImagePlus className="h-7 w-7" />}
            <span className="text-xs">{busy ? "Upload…" : "Klicken oder Datei hierher ziehen"}</span>
          </div>
        )}
      </div>
      <input
        ref={inputRef} type="file" accept="image/*" className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
}

function StepHeader({ step, total }) {
  const pct = Math.round((step / total) * 100);
  return (
    <div className="mb-6" data-testid="wizard-progress">
      <div className="mb-2 flex items-center justify-between text-xs uppercase tracking-[0.2em] text-zinc-500">
        <span>Schritt {step} von {total}</span>
        <span data-testid="wizard-progress-pct">{pct}%</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-zinc-900">
        <div
          className="h-full bg-gradient-to-r from-red-600 to-orange-500 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {STEPS.map((s) => (
          <span
            key={s.id}
            data-testid={`wizard-step-pill-${s.key}`}
            className={classNames(
              "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider",
              s.id === step
                ? "border-red-500 bg-red-500/15 text-red-300"
                : s.id < step
                  ? "border-emerald-600/40 bg-emerald-600/10 text-emerald-300"
                  : "border-zinc-800 bg-zinc-950 text-zinc-500"
            )}
          >
            {s.id < step ? <Check className="h-3 w-3" /> : <s.icon className="h-3 w-3" />}
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function NavRow({ step, total, onPrev, onNext, onSave, dirty, busy, canFinish }) {
  return (
    <div className="mt-8 flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex gap-2">
        <Button variant="outline" disabled={step <= 1 || busy} onClick={onPrev} className="border-zinc-800 bg-transparent text-white hover:bg-zinc-900" data-testid="wizard-prev">
          <ChevronLeft className="mr-1 h-4 w-4" /> Zurück
        </Button>
        <Button variant="outline" disabled={!dirty || busy} onClick={onSave} className="border-amber-500/40 bg-amber-500/5 text-amber-300 hover:bg-amber-500/10" data-testid="wizard-save">
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} Speichern
        </Button>
      </div>
      <Button
        onClick={onNext}
        disabled={busy}
        className="bg-gradient-to-r from-red-600 to-orange-500 text-white hover:from-red-500 hover:to-orange-400"
        data-testid="wizard-next"
      >
        {step >= total ? (canFinish ? "Fertigstellen" : "Speichern & Schliessen") : "Weiter"}
        <ChevronRight className="ml-1 h-4 w-4" />
      </Button>
    </div>
  );
}

export default function DealerProfile() {
  const { user } = useAuth();
  const navigate = useLocaleNavigate();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [profile, setProfile] = useState(null);
  const [step, setStep] = useState(1);
  const [dirty, setDirty] = useState(false);
  const [form, setForm] = useState({
    company_name: "", slug: "", description: "", website: "",
    phone: "", whatsapp: "", address: "", canton: "", since_year: "",
    logo_url: "", banner_url: "",
    hours: DEFAULT_HOURS,
    socials: { facebook: "", instagram: "", linkedin: "", youtube: "", tiktok: "" },
  });
  const [slugCheck, setSlugCheck] = useState(null);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    api.dealerMe()
      .then((d) => {
        setProfile(d);
        setForm({
          company_name: d.company_name || "",
          slug: d.slug || "",
          description: d.description || "",
          website: d.website || "",
          phone: d.phone || "",
          whatsapp: d.whatsapp || "",
          address: d.address || "",
          canton: d.canton || "",
          since_year: d.since_year ? String(d.since_year) : "",
          logo_url: d.logo_url || "",
          banner_url: d.banner_url || "",
          hours: Array.isArray(d.hours) && d.hours.length === 7 ? d.hours : DEFAULT_HOURS,
          socials: { facebook: "", instagram: "", linkedin: "", youtube: "", tiktok: "", ...(d.socials || {}) },
        });
      })
      .catch((err) => {
        if (err?.response?.status === 403) {
          toast.error("Kein Dealer-Konto — bitte Dealer-Abo abschliessen");
          navigate("/pricing");
        } else {
          toast.error(err?.response?.data?.detail || "Fehler");
        }
      })
      .finally(() => setLoading(false));
  }, [user]);  // eslint-disable-line react-hooks/exhaustive-deps

  const completion = profile?.completion?.percent ?? 0;

  const update = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };
  const updateSocial = (k, v) => { setForm((f) => ({ ...f, socials: { ...f.socials, [k]: v } })); setDirty(true); };

  const checkSlug = async (slug) => {
    if (!slug) { setSlugCheck(null); return; }
    try {
      const res = await api.dealerCheckSlug(slug);
      setSlugCheck(res);
    } catch { setSlugCheck(null); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        company_name: form.company_name || null,
        slug: form.slug || null,
        description: form.description || null,
        website: form.website || null,
        phone: form.phone || null,
        whatsapp: form.whatsapp || null,
        address: form.address || null,
        canton: form.canton || null,
        since_year: form.since_year ? Number(form.since_year) : null,
        logo_url: form.logo_url || null,
        banner_url: form.banner_url || null,
        hours: form.hours,
        socials: Object.fromEntries(
          Object.entries(form.socials).filter(([_k, v]) => v && v.trim())
        ),
      };
      const updated = await api.dealerPatch(payload);
      setProfile(updated);
      setDirty(false);
      toast.success("Gespeichert");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
      return false;
    } finally { setBusy(false); }
  };

  const handleNext = async () => {
    if (dirty) {
      const ok = await save();
      if (!ok) return;
    }
    if (step < STEPS.length) setStep((s) => s + 1);
    else toast.success("Profil ist fertig");
  };

  const handlePrev = () => {
    if (step > 1) setStep((s) => s - 1);
  };

  const publicUrl = useMemo(
    () => (form.slug ? `${window.location.origin}/de/dealers/${form.slug}` : null),
    [form.slug]
  );

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-zinc-500">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8" data-testid="dealer-wizard-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-red-500">Händlerprofil</p>
          <h1 className="mt-2 font-display text-3xl font-bold text-white sm:text-4xl">Profil einrichten</h1>
          <p className="mt-2 text-sm text-zinc-400">
            <span data-testid="profile-completion">Profil zu {completion}% vollständig</span> · Autosave aktiv
          </p>
        </div>
        {publicUrl && (
          <a
            href={publicUrl} target="_blank" rel="noopener noreferrer"
            className="hidden items-center gap-1 rounded-full border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-xs text-zinc-300 hover:border-red-500/40 hover:text-red-300 sm:inline-flex"
            data-testid="dealer-public-link"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Öffentlich
          </a>
        )}
      </div>

      <StepHeader step={step} total={STEPS.length} />

      <Card className="border-zinc-800 bg-zinc-950 p-6">
        {/* STEP 1 — Identity */}
        {step === 1 && (
          <div className="space-y-5" data-testid="wizard-step-identity">
            <div>
              <Label className="text-zinc-200">Händlername *</Label>
              <Input
                value={form.company_name}
                onChange={(e) => update({ company_name: e.target.value })}
                placeholder="z.B. Müller Automobile AG"
                className="mt-2 border-zinc-800 bg-black text-white"
                maxLength={200}
                data-testid="field-company_name"
              />
            </div>
            <div>
              <Label className="text-zinc-200">URL-Slug</Label>
              <div className="mt-2 flex items-center gap-2">
                <span className="rounded-l-md border border-r-0 border-zinc-800 bg-zinc-900 px-3 py-2 text-xs text-zinc-500">/dealers/</span>
                <Input
                  value={form.slug}
                  onChange={(e) => update({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").slice(0, 80) })}
                  onBlur={(e) => checkSlug(e.target.value)}
                  placeholder="mueller-automobile"
                  className="rounded-l-none border-zinc-800 bg-black text-white"
                  maxLength={80}
                  data-testid="field-slug"
                />
              </div>
              {slugCheck && (
                <p className={`mt-2 text-xs ${slugCheck.available ? "text-emerald-400" : "text-amber-400"}`} data-testid="slug-check-msg">
                  {slugCheck.available
                    ? `✓ "${slugCheck.normalized}" ist verfügbar`
                    : `"${slugCheck.normalized}" ist vergeben — Vorschlag: ${slugCheck.suggested}`}
                </p>
              )}
            </div>
            <ImageDrop
              value={form.logo_url}
              onChange={(v) => update({ logo_url: v })}
              label="Logo (quadratisch empfohlen)"
              aspect="1/1"
              testid="upload-logo"
            />
          </div>
        )}

        {/* STEP 2 — Story */}
        {step === 2 && (
          <div className="space-y-5" data-testid="wizard-step-story">
            <div>
              <Label className="text-zinc-200">Über uns / Beschreibung</Label>
              <Textarea
                rows={6}
                value={form.description}
                onChange={(e) => update({ description: e.target.value })}
                placeholder="Was macht euch besonders? Eure Spezialgebiete, Geschichte, Werte…"
                className="mt-2 border-zinc-800 bg-black text-white"
                maxLength={2000}
                data-testid="field-description"
              />
              <p className="mt-1 text-xs text-zinc-500">{form.description.length} / 2000 Zeichen</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-zinc-200">Gegründet</Label>
                <Input
                  type="number" min={1900} max={2100}
                  value={form.since_year}
                  onChange={(e) => update({ since_year: e.target.value })}
                  placeholder="1987"
                  className="mt-2 border-zinc-800 bg-black text-white"
                  data-testid="field-since_year"
                />
              </div>
            </div>
          </div>
        )}

        {/* STEP 3 — Contact */}
        {step === 3 && (
          <div className="space-y-5" data-testid="wizard-step-contact">
            <div>
              <Label className="text-zinc-200 inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> Telefonnummer</Label>
              <Input
                value={form.phone}
                onChange={(e) => update({ phone: e.target.value })}
                placeholder="+41 79 123 45 67"
                className="mt-2 border-zinc-800 bg-black text-white"
                data-testid="field-phone"
              />
            </div>
            <div>
              <Label className="text-zinc-200 inline-flex items-center gap-1.5"><MessageCircle className="h-3.5 w-3.5 text-emerald-400" /> WhatsApp</Label>
              <Input
                value={form.whatsapp}
                onChange={(e) => update({ whatsapp: e.target.value })}
                placeholder="+41 79 123 45 67 (im internationalen Format)"
                className="mt-2 border-zinc-800 bg-black text-white"
                data-testid="field-whatsapp"
              />
              <p className="mt-1 text-xs text-zinc-500">Wird auf deiner Händlerseite als WhatsApp-Button angezeigt.</p>
            </div>
            <div>
              <Label className="text-zinc-200 inline-flex items-center gap-1.5"><Globe className="h-3.5 w-3.5" /> Website</Label>
              <Input
                type="url"
                value={form.website}
                onChange={(e) => update({ website: e.target.value })}
                placeholder="https://mueller-automobile.ch"
                className="mt-2 border-zinc-800 bg-black text-white"
                data-testid="field-website"
              />
            </div>
          </div>
        )}

        {/* STEP 4 — Location + Banner */}
        {step === 4 && (
          <div className="space-y-5" data-testid="wizard-step-location">
            <div>
              <Label className="text-zinc-200">Adresse</Label>
              <Input
                value={form.address}
                onChange={(e) => update({ address: e.target.value })}
                placeholder="Bahnhofstrasse 1, 8001 Zürich"
                className="mt-2 border-zinc-800 bg-black text-white"
                data-testid="field-address"
              />
            </div>
            <div>
              <Label className="text-zinc-200">Kanton</Label>
              <Input
                value={form.canton}
                onChange={(e) => update({ canton: e.target.value.toUpperCase().slice(0, 2) })}
                placeholder="ZH"
                className="mt-2 w-20 border-zinc-800 bg-black text-white"
                maxLength={2}
                data-testid="field-canton"
              />
            </div>
            <ImageDrop
              value={form.banner_url}
              onChange={(v) => update({ banner_url: v })}
              label="Cover-Bild / Garage-Banner (16:9 empfohlen)"
              aspect="16/9"
              testid="upload-banner"
            />
          </div>
        )}

        {/* STEP 5 — Hours */}
        {step === 5 && (
          <div className="space-y-3" data-testid="wizard-step-hours">
            <p className="mb-2 text-sm text-zinc-400">Wann seid ihr für Kunden erreichbar?</p>
            {form.hours.map((h, idx) => (
              <div key={h.day} className="grid grid-cols-[60px_70px_1fr_8px_1fr] items-center gap-2" data-testid={`hours-row-${h.day}`}>
                <span className="text-sm font-semibold text-zinc-300">{WEEKDAYS.find((d) => d.key === h.day)?.label}</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const copy = [...form.hours];
                    copy[idx] = { ...copy[idx], closed: !copy[idx].closed };
                    update({ hours: copy });
                  }}
                  className={classNames(
                    "border-zinc-800 bg-transparent text-xs hover:bg-zinc-900",
                    h.closed ? "text-zinc-500" : "text-emerald-300"
                  )}
                  data-testid={`hours-toggle-${h.day}`}
                >
                  {h.closed ? "Zu" : "Offen"}
                </Button>
                <Input
                  type="time" disabled={h.closed} value={h.open}
                  onChange={(e) => {
                    const copy = [...form.hours];
                    copy[idx] = { ...copy[idx], open: e.target.value };
                    update({ hours: copy });
                  }}
                  className="border-zinc-800 bg-black text-white disabled:opacity-40"
                  data-testid={`hours-open-${h.day}`}
                />
                <span className="text-zinc-500">–</span>
                <Input
                  type="time" disabled={h.closed} value={h.close}
                  onChange={(e) => {
                    const copy = [...form.hours];
                    copy[idx] = { ...copy[idx], close: e.target.value };
                    update({ hours: copy });
                  }}
                  className="border-zinc-800 bg-black text-white disabled:opacity-40"
                  data-testid={`hours-close-${h.day}`}
                />
              </div>
            ))}
          </div>
        )}

        {/* STEP 6 — Social */}
        {step === 6 && (
          <div className="space-y-4" data-testid="wizard-step-social">
            <p className="text-sm text-zinc-400">Verlinke eure Social-Media-Profile (optional).</p>
            {[
              ["facebook", "Facebook", "https://facebook.com/…"],
              ["instagram", "Instagram", "https://instagram.com/…"],
              ["linkedin", "LinkedIn", "https://linkedin.com/company/…"],
              ["youtube", "YouTube", "https://youtube.com/@…"],
              ["tiktok", "TikTok", "https://tiktok.com/@…"],
            ].map(([k, label, ph]) => (
              <div key={k}>
                <Label className="text-zinc-200">{label}</Label>
                <Input
                  value={form.socials[k] || ""}
                  onChange={(e) => updateSocial(k, e.target.value)}
                  placeholder={ph}
                  className="mt-2 border-zinc-800 bg-black text-white"
                  data-testid={`field-social-${k}`}
                />
              </div>
            ))}
          </div>
        )}

        {/* STEP 7 — Review */}
        {step === 7 && (
          <div className="space-y-5" data-testid="wizard-step-review">
            {form.banner_url && (
              <div className="overflow-hidden rounded-xl">
                <img src={form.banner_url} alt="" className="h-48 w-full object-cover" />
              </div>
            )}
            <div className="flex items-center gap-4">
              {form.logo_url && (
                <img src={form.logo_url} alt="" className="h-16 w-16 rounded-full object-cover ring-2 ring-zinc-800" />
              )}
              <div>
                <h2 className="font-display text-xl font-bold text-white" data-testid="review-company">{form.company_name || "(noch kein Name)"}</h2>
                {form.since_year && <p className="text-xs text-zinc-500">Seit {form.since_year}</p>}
              </div>
            </div>
            {form.description && (
              <p className="text-sm text-zinc-300">{form.description}</p>
            )}
            <div className="grid gap-3 text-sm text-zinc-400 sm:grid-cols-2">
              {form.address && <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4" /> {form.address}{form.canton ? ` · ${form.canton}` : ""}</span>}
              {form.phone && <span className="inline-flex items-center gap-2"><Phone className="h-4 w-4" /> {form.phone}</span>}
              {form.whatsapp && <span className="inline-flex items-center gap-2"><MessageCircle className="h-4 w-4 text-emerald-400" /> {form.whatsapp}</span>}
              {form.website && <span className="inline-flex items-center gap-2"><Globe className="h-4 w-4" /> {form.website}</span>}
            </div>
            {publicUrl && (
              <Button asChild className="w-full bg-gradient-to-r from-red-600 to-orange-500 text-white hover:from-red-500 hover:to-orange-400">
                <a href={publicUrl} target="_blank" rel="noopener noreferrer" data-testid="review-public-link">
                  <Eye className="mr-1.5 h-4 w-4" /> Öffentliche Seite öffnen
                </a>
              </Button>
            )}
          </div>
        )}

        <NavRow
          step={step}
          total={STEPS.length}
          onPrev={handlePrev}
          onNext={handleNext}
          onSave={save}
          dirty={dirty}
          busy={busy}
          canFinish={completion >= 80}
        />
      </Card>

      {profile?.dealer_verified && (
        <div className="mt-6 flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-3 text-sm text-emerald-300">
          <ShieldCheck className="h-4 w-4" /> Verifiziertes CarHive-Konto
        </div>
      )}
    </div>
  );
}
